import moment from 'moment-timezone';
import isExists from './isExists';

/**
 * Single source of truth for the global (Topnav) date filter.
 *
 * There are two distinct things stored in localStorage:
 *
 *  1. The ACTIVE range — `start_date`, `end_date`, `date_chosen_label`, `setdate`.
 *     This is what every list/stats screen reads synchronously on mount via
 *     getSelectedDate(). It is the range currently applied on screen.
 *
 *  2. The account PREFERENCE — `date_filter_pref` ({label[, start_date, end_date]}).
 *     A client-side cache of the range the user genuinely chose (mirrors the server's
 *     `usersettings.date_filter`). It is what the app should fall back to everywhere.
 *
 * Normally the active range mirrors the preference. A deep link (`?date_range=all_time`
 * from the analytics emails) temporarily overrides the ACTIVE range with All Time so
 * older subscriptions aren't hidden — WITHOUT touching the preference. That override is
 * scoped to the subscription section: leaving it restores the active range from the
 * preference (see Topnav.componentDidUpdate). A full page load has no such client-side
 * navigation, so index.js re-seeds both from the server on /api/verify.
 *
 * Preset picks ("Last 30 Days", "This Month", ...) are stored as their label and the
 * dates are RECOMPUTED on every read, so a saved preset keeps meaning the same thing
 * on a later day instead of freezing to the window that was current when it was
 * picked. A custom range is stored as absolute dates and kept until it is changed.
 */

export const DEFAULT_RANGE_LABEL = 'Last 30 Days';
export const PREFERENCE_KEY = 'date_filter_pref';

/**
 * Presets offered by the picker. Keys are the labels shown in the dropdown and
 * stored verbatim as `date_chosen_label`; key order is the order they are listed in.
 */
export function getPresetRanges() {
    return {
        'Last 30 Days': [
            moment().subtract(29, 'days').toDate(),
            moment().toDate()
        ],
        'Last 7 Days': [
            moment().subtract(6, 'days').toDate(),
            moment().toDate()
        ],
        'Last Month': [
            moment().subtract(1, 'months').startOf('month').toDate(),
            moment().subtract(1, 'months').endOf('month').toDate()
        ],
        'This Month': [
            moment().startOf('month').toDate(),
            moment().toDate()
        ],
        'All Time': [
            moment('2017-01-01').toDate(),
            moment().toDate()
        ],
        'Today': [
            moment().toDate(),
            moment().toDate()
        ],
        'Yesterday': [
            moment().subtract(1, 'days').toDate(),
            moment().subtract(1, 'days').toDate()
        ]
    };
}

/**
 * Dates for a preset label, or null when the label is a custom range.
 */
export function resolvePresetRange(label) {
    var ranges = getPresetRanges();
    if (!isExists(label) || !ranges[label]) {
        return null;
    }
    return { start_date: ranges[label][0], end_date: ranges[label][1] };
}

/**
 * Persist the range in localStorage the way a manual pick does.
 */
export function storeDateRange(label, start_date, end_date) {
    localStorage.setItem('start_date', start_date);
    localStorage.setItem('end_date', end_date);
    localStorage.setItem('setdate', moment().toDate());
    localStorage.setItem('date_chosen_label', label);
    return { start_date, end_date };
}

/**
 * The range the app should currently be filtered by, normalized back into
 * localStorage so getSelectedDate() consumers see the same window.
 */
export function readStoredDateRange() {
    var label = localStorage.getItem('date_chosen_label');
    var preset = resolvePresetRange(label);

    if (preset) {
        return storeDateRange(label, preset.start_date, preset.end_date);
    }

    // Custom range — keep exactly the dates that were picked.
    var stored_start = localStorage.getItem('start_date');
    var stored_end = localStorage.getItem('end_date');
    if (isExists(label) && isExists(stored_start) && isExists(stored_end)) {
        return storeDateRange(label, moment(stored_start).toDate(), moment(stored_end).toDate());
    }

    var fallback = resolvePresetRange(DEFAULT_RANGE_LABEL);
    return storeDateRange(DEFAULT_RANGE_LABEL, fallback.start_date, fallback.end_date);
}

/**
 * Normalize a raw preference (server value or a manual pick) into the descriptor
 * cached under PREFERENCE_KEY: presets keep only their label (dates recomputed on
 * read), custom ranges keep absolute YYYY-MM-DD dates. Unusable input -> the default.
 */
function normalizePreference(saved) {
    if (saved && isExists(saved.label)) {
        if (resolvePresetRange(saved.label)) {
            return { label: saved.label };
        }
        var start_date = moment(saved.start_date, 'YYYY-MM-DD', true);
        var end_date = moment(saved.end_date, 'YYYY-MM-DD', true);
        if (start_date.isValid() && end_date.isValid()) {
            return {
                label: saved.label,
                start_date: start_date.format('YYYY-MM-DD'),
                end_date: end_date.format('YYYY-MM-DD')
            };
        }
    }
    return { label: DEFAULT_RANGE_LABEL };
}

/**
 * Cache the genuine account preference client-side so it survives in-app navigation
 * (there is no server round-trip between pages). Called from index.js on load with the
 * server value, and from the Topnav on every manual pick.
 */
export function storePreference(saved) {
    var descriptor = normalizePreference(saved);
    try {
        localStorage.setItem(PREFERENCE_KEY, JSON.stringify(descriptor));
    } catch (e) {
        // ignore serialization / quota issues — the active range is the source that matters
    }
    return descriptor;
}

/**
 * Read the cached preference descriptor, or the default when none is stored.
 */
export function readPreference() {
    try {
        var raw = localStorage.getItem(PREFERENCE_KEY);
        if (raw) {
            var parsed = JSON.parse(raw);
            if (parsed && isExists(parsed.label)) {
                return parsed;
            }
        }
    } catch (e) {
        // fall through to default
    }
    return { label: DEFAULT_RANGE_LABEL };
}

/**
 * Concrete Date objects for a preference descriptor (preset recomputed against today,
 * custom parsed from its stored dates).
 */
function resolvePreferenceRange(descriptor) {
    var preset = resolvePresetRange(descriptor.label);
    if (preset) {
        return preset;
    }
    var start_date = moment(descriptor.start_date, 'YYYY-MM-DD', true);
    var end_date = moment(descriptor.end_date, 'YYYY-MM-DD', true);
    if (start_date.isValid() && end_date.isValid()) {
        return { start_date: start_date.toDate(), end_date: end_date.toDate() };
    }
    return resolvePresetRange(DEFAULT_RANGE_LABEL);
}

/**
 * Make the ACTIVE range mirror the cached preference. Returns
 * { label, start_date, end_date } (dates as Date objects) so the caller can dispatch it.
 */
export function applyPreferenceToActive() {
    var descriptor = readPreference();
    var range = resolvePreferenceRange(descriptor);
    storeDateRange(descriptor.label, range.start_date, range.end_date);
    return { label: descriptor.label, start_date: range.start_date, end_date: range.end_date };
}

/**
 * Whether the active range already equals the cached preference — i.e. there is no
 * transient override (e.g. an all-time deep link) that needs restoring.
 */
export function activeMatchesPreference() {
    return localStorage.getItem('date_chosen_label') === readPreference().label;
}

/**
 * Shape the range for the save endpoint (dates normalized to YYYY-MM-DD).
 */
export function dateRangePayload(label, start_date, end_date) {
    return {
        label: label,
        start_date: moment(start_date).format('YYYY-MM-DD'),
        end_date: moment(end_date).format('YYYY-MM-DD')
    };
}
